import { EditorState, Modifier, RichUtils } from 'draft-js'
import {
	myKeyBindingFn,
	findInlineTeXEntities,
} from './utils'
import loadMathJax from './mathjax/loadMathJax'
import initCompletion from './mathjax/completion'
import insertTeX from './modifiers/insertTeX'
import InlineTeX from './components/InlineTeX'
import TeXBlock from './components/TeXBlock'
import { InsertFormulaButton } from './components/InsertFormulaButton';
import { createToolbarButtons } from 'lib/editor/DraftEditor/plugins/createToolbarButtons';
import { isCurrentBlockEmpty } from 'lib/editor/DraftEditor/plugins/formulaPlugin/modifiers/utils';

const defaultOptions = {
	macros: {},
	completion: 'auto',
	buttons: ['formula'],
};

const BUTTON_MAP = {
	formula: InsertFormulaButton,
};

/**
 * @returns {DraftEditorPlugin}
 */
export function createFormulaPlugin(options = {}) {
	const {
		buttons,
		macros,
		completion,
		script,
		mathjaxConfig,
	} = { ...defaultOptions, options };

	loadMathJax({ macros, script, mathjaxConfig })

	const formulaStore = {
		getEditorState: undefined,
		setEditorState: undefined,
		getEditorReadOnly: undefined,
		setEditorReadOnly: undefined,
		getEditorRef: undefined,
		completion: initCompletion(completion, macros),
		teXToUpdate: {},
	};

	const createFormulaStore = (store) => {
		Object.assign(formulaStore, store);
		return formulaStore;
	}

	const _insertTeX = (store, block = false) => {
		const editorState = store.getEditorState()
		store.setEditorState(
			insertTeX(editorState, block),
		)
	}

	const insertChar = (char) => {
		const editorState = formulaStore.getEditorState()
		const sel = editorState.getSelection()
		const offset = sel.getStartOffset() - 1
		const newContentState = Modifier.replaceText(
			editorState.getCurrentContent(),
			sel.merge({
				anchorOffset: offset,
				focusOffset: offset + 1,
			}),
			char,
		)
		formulaStore.setEditorState(
			EditorState.push(editorState, newContentState, 'insert-characters'),
		)
	}

	const keyBinding = (editorState, evt) => myKeyBindingFn(() => editorState)(evt);

	const getBlockRenderInfo = (block, editorStore) => {
		if (
			block.getType() === 'atomic' && block.getData().get('mathjax')
		) {
			return {
				component: TeXBlock,
				editable: false,
				props: { getStore: () => createFormulaStore(editorStore) },
			}
		}
		return null
	}

	// l'utilisation des flèches gauche ou droite
	// amène le curseur sur une formule
	const updateTeX = (store, key, dir) => {
		// le composant associé à la formule
		// se sert de cet indicateur
		// pour se reconnaître
		// cf.componentWillReceiveProps
		store.teXToUpdate = { key, dir }
		const editorState = store.getEditorState()
		store.setEditorState(
			EditorState.forceSelection(
				editorState,
				editorState.getSelection(),
			),
		)
	}

	const handleCommand = (store, command, evt) => {
		store = createFormulaStore(store);
		if (command === 'insert-texblock') {
			_insertTeX(store, true)
			return 'handled'
		}
		if (command === 'insert-inlinetex') {
			_insertTeX(store)
			return 'handled'
		}
		// command de la forme 'enter-inline-math-<dir>-<entityKey>',
		// lancée lorsque l'utilisateur déplace le curseur
		// sur une formule à l'aide des flèches gauche/droite(dir:l ou r)
		if (command.slice(0, 16) === 'update-inlinetex') {
			const dir = command.slice(17, 18)
			const entityKey = command.slice(19)
			updateTeX(store, entityKey, dir)
			return 'handled'
		}
		if (command.slice(0, 15) === 'update-texblock') {
			const dir = command.slice(16, 17)
			const blockKey = command.slice(18)

			updateTeX(store, lockKey, dir)
			return 'handled'
		}
		if (command.slice(0, 11) === 'insert-char') {
			const char = command.slice(12)
			insertChar(char)
			return 'handled'
		}
		if (evt && evt.altKey && (command === 'arrow-left' || command === 'arrow-right')) {
			const direction = command === 'arrow-right' ? 'r' : 'l'
			const selection = store.getEditorState().getSelection()
			const content = store.getEditorState().getCurrentContent()
			if (!selection.isCollapsed()) {
				return 'not-handled';
			}
			const offset = selection.getStartOffset();
			const blockKey = selection.getStartKey();
			const block = content.getBlockForKey(blockKey);
			if (block.getLength() === offset && direction === 'r') {
				const nextBlock = content.getBlockAfter(blockKey)
				if (nextBlock && nextBlock.getType() === 'atomic' && nextBlock.getData().get('mathjax')) {
					updateTeX(store, nextBlock.getKey(), direction);
					return 'handled';
				}
			}
			if (offset === 0 && direction === 'l') {
				const nextBlock = content.getBlockBefore(blockKey)
				if (nextBlock && nextBlock.getType() === 'atomic' && nextBlock.getData().get('mathjax')) {
					updateTeX(store, nextBlock.getKey(), direction);
					return 'handled';
				}
			}
			const entityKey = block.getEntityAt(offset - (direction === 'l' ? 1 : 0))
			if (entityKey && content.getEntity(entityKey).getType() === 'INLINETEX') {
				updateTeX(store, entityKey, direction);
				return 'handled';
			}
		}
		return 'not-handled'
	};

	return {
		name: 'formula',
		getToolbarButtons(props) {
			if (props.env !== 'root') {
				return null;
			}
			const editorState = props.store.getEditorState();
			// 代码块不支持图片
			if (RichUtils.getCurrentBlockType(editorState) === 'code-block') {
				return null;
			}
			return createToolbarButtons(BUTTON_MAP, buttons, {
				...props,
				onInsert() {
					const store = props.store;
					const editorState = store.getEditorState();
					const content = editorState.getCurrentContent();
					const selection = editorState.getSelection();
					const empty = isCurrentBlockEmpty(content, selection);
					handleCommand(store, empty ? 'insert-texblock' : 'insert-inlinetex');
				}
			});
		},
		getDraftDecorators: (editorStore) => [{
			strategy: findInlineTeXEntities,
			component: InlineTeX,
			props: {
				getStore: () => createFormulaStore(editorStore),
			},
		}],
		keyBinding,
		handleCommand,
		getBlockRenderInfo,
	};
}




// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/formulaPlugin/createFormulaPlugin.js